import { CONFIG } from '../config.js';
import { laneNeeds, pieceExtent } from '../world/Generator.js';

/**
 * Piloto automático para pruebas (?autopilot=1 en el navegador, y scripts/simulate.mjs en Node):
 * elige carril, salta, se desliza y gira a tiempo. Sirve para verificar que el camino generado
 * siempre es superable y para medir rendimiento de forma reproducible.
 */
const JUMP_LEAD = 0.22; // segundos antes del tronco
const SLIDE_LEAD = 0.27; // segundos antes de la rama
const TURN_LEAD = 0.25; // segundos antes de la esquina
const PLAN_LEAD = 1.2; // segundos antes de una fila para elegir carril
const AIR_TIME = (2 * CONFIG.player.jumpVelocity) / CONFIG.player.gravity;

const COST = { free: 0, low: 1, high: 1, pit: 1, wall: Infinity };

/** Metros en los que la fila ocupa el carril (incluye fosos y columnas largas). */
function rowSpan(row, lane) {
  let a = Infinity;
  let b = -Infinity;
  for (const p of row.pieces) {
    if (lane !== undefined && !(p.lane <= lane && lane < p.lane + p.span)) continue;
    const [e0, e1] = pieceExtent(p);
    a = Math.min(a, row.s + e0);
    b = Math.max(b, row.s + e1);
  }
  return [a, b];
}

export class Autopilot {
  constructor(rand = Math.random) {
    this.rand = rand;
  }

  update(p) {
    if (p.state !== 'running') return;
    const seg = p.segment;

    if ((seg.turn || seg.tee) && !p.queuedTurn && p.toCorner < p.speed * TURN_LEAD) {
      p.requestLateral(seg.tee ? (this.rand() < 0.5 ? -1 : 1) : seg.turn);
      return;
    }

    // Filas que aún no se han superado y están a menos de PLAN_LEAD segundos. Mandan las que ya
    // se están atravesando y la siguiente (coste duro); las de más allá solo orientan (coste blando).
    const horizon = p.s + p.speed * PLAN_LEAD;
    const imminent = p.s + p.speed * 0.25 + 1;
    const cost = { [-1]: 0, 0: 0, 1: 0 };
    const nowBlocked = new Set(); // carriles con algo encima ahora mismo: no se cruzan ni se pisan
    const nowWall = new Set(); // …y de ellos, los que tienen roca (o un foso largo)
    let hardLeft = 1; // filas futuras que aún cuentan como duras
    for (const row of seg.rows) {
      const [a, b] = rowSpan(row);
      if (b + 0.8 < p.s || a > horizon) continue;
      const needs = laneNeeds(row.pieces);
      const current = a <= p.s + 0.5;
      const hard = current || hardLeft-- > 0;
      for (const l of [-1, 0, 1]) {
        const c = COST[needs[l]];
        cost[l] += hard ? c : Math.min(c, 5) * 0.5;
        if (needs[l] === 'free') continue;
        const [la, lb] = rowSpan(row, l);
        if (la < imminent && lb > p.s - 1) {
          nowBlocked.add(l);
          if (needs[l] === 'wall') nowWall.add(l);
        }
      }
    }

    let best = p.lane;
    let bestCost = Infinity;
    for (const lane of [-1, 0, 1]) {
      const c = cost[lane] + Math.abs(lane - p.lane) * 0.4;
      if (c < bestCost) {
        bestCost = c;
        best = lane;
      }
    }
    const step = Math.sign(best - p.lane);
    const target = p.lane + step;
    // Si se va a chocar donde está, escapa aunque el carril de al lado pida saltar o agacharse.
    const escape = nowWall.has(p.lane) && !nowWall.has(target);
    if (step && (!nowBlocked.has(target) || escape)) p.requestLateral(step);

    // Salto / deslizamiento según lo que tenga delante en su carril.
    for (const row of seg.rows) {
      const piece = row.pieces.find((q) => q.lane <= p.lane && p.lane < q.lane + q.span);
      if (!piece) continue;
      const [a, b] = rowSpan(row, p.lane);
      if (b < p.s) continue;
      if (a - p.s > p.speed * PLAN_LEAD) break;
      if (piece.type === 'low' && row.s - p.s < p.speed * JUMP_LEAD && p.grounded) p.requestJump();
      if (piece.type === 'high' && row.s - p.s < p.speed * SLIDE_LEAD && !p.sliding) p.requestSlide();
      // Foso: despegar para que el centro del vuelo caiga en el centro del foso.
      if (piece.type === 'pit' && p.grounded && p.s >= (a + b) / 2 - (p.speed * AIR_TIME) / 2) p.requestJump();
      break;
    }
  }
}
